import React from "react";
import { Link } from "react-router-dom";
import { trackEvent } from "../../lib/analytics";
import SessionUrgencyTimer from "./SessionUrgencyTimer";

const PUB = process.env.PUBLIC_URL || "";

/**
 * <TieUpLooseEndsBanner />
 *
 * Sitewide promo strip for the FREE Tie Up Loose Ends guide. Drops in below
 * the testimonials block on every product page (excluding the booking, podcast,
 * and the page itself).
 *
 *   • Floating, glowing 3-D book hero (transparent PNG)
 *   • Continuous gentle float + radial glow pulse
 *   • Gold "FREE" pill + clear CTA → /tie-up-loose-ends
 */
export default function TieUpLooseEndsBanner({ source = "page" }) {
  return (
    <section
      data-testid="tie-up-loose-ends-banner"
      className="relative py-20 md:py-28 overflow-hidden border-t border-white/5"
      style={{
        background:
          "radial-gradient(ellipse 60% 70% at 30% 50%, rgba(212,175,55,0.10), transparent 65%), linear-gradient(180deg, #04060c 0%, #08080d 100%)",
      }}
    >
      {/* Decorative grain + sparkles */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.035] [background-image:radial-gradient(rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:20px_20px]" />

      <div className="max-w-6xl mx-auto px-6 md:px-12 relative">
        <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Animated book with halo */}
          <div className="relative flex items-center justify-center min-h-[360px] md:min-h-[440px]">
            <div
              aria-hidden="true"
              className="absolute w-[320px] h-[320px] md:w-[420px] md:h-[420px] rounded-full pointer-events-none tul-halo"
              style={{
                background:
                  "radial-gradient(circle, rgba(212,175,55,0.35) 0%, rgba(212,175,55,0.12) 35%, transparent 65%)",
                filter: "blur(8px)",
              }}
            />
            <img
              src={`${PUB}/tie-up-loose-ends/book-3d.png`}
              alt="Tie Up Loose Ends — free strategic website guide"
              loading="lazy"
              data-testid="tie-up-loose-ends-banner-image"
              className="relative z-10 w-[260px] md:w-[340px] h-auto select-none tul-float drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)]"
              draggable="false"
            />
          </div>

          {/* Copy + CTA */}
          <div>
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-gold/85 px-3 py-1.5 rounded-full border border-gold/30 bg-black/30">
              <span
                className="px-2 py-0.5 rounded-full text-[10px] font-bold text-black"
                style={{ background: "linear-gradient(135deg, #f3d172 0%, #D4AF37 60%, #a07e1f 100%)" }}
              >
                FREE
              </span>
              Strategic Website Guide
            </span>

            <h2 className="mt-5 font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-tight">
              Stop your website from <span className="bk-gold-text italic">bleeding revenue</span>.
            </h2>

            <p className="mt-5 text-white/65 leading-relaxed max-w-xl">
              <span className="text-white/85">Tie Up Loose Ends</span> is our free strategic guide
              for growth-stage business owners. Diagnose what's leaking, decide what to build,
              implement it right, and regenerate revenue from your website for years —
              without overspending or guessing.
            </p>

            <ul className="mt-5 space-y-2 text-sm text-white/75 max-w-md">
              {[
                "Find the hidden revenue leaks on your site",
                "Match your website investment to your stage",
                "Build a system that compounds, not a digital brochure",
              ].map((b) => (
                <li key={b} className="flex items-start gap-2.5">
                  <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex items-center gap-5 flex-wrap">
              <Link
                to="/tie-up-loose-ends"
                onClick={() => trackEvent("tie_up_loose_ends_banner_click", { metadata: { source } })}
                data-testid="tie-up-loose-ends-banner-cta"
                className="inline-flex items-center px-7 py-3.5 rounded-full text-xs uppercase tracking-[0.22em] font-bold text-black hover:scale-[1.02] transition-transform"
                style={{
                  background: "linear-gradient(135deg, #f3d172 0%, #D4AF37 60%, #a07e1f 100%)",
                  boxShadow: "0 0 28px rgba(212,175,55,0.40)",
                }}
              >
                Get the Free Guide
                <span className="ml-2">→</span>
              </Link>
              <span className="text-xs uppercase tracking-[0.22em] text-white/45">
                100% free · no credit card
              </span>
            </div>

            <div className="mt-8">
              <SessionUrgencyTimer
                storageKey={`tul-banner-${source}`}
                label="Free download · this session only · expires in"
                variant="compact"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
